import { io, type Socket } from 'socket.io-client';

/** Where the channel takes its token: `Api.accessToken`. */
export type AccessTokenSource = (rejected?: string | null) => Promise<string | null>;

/**
 * What a refused handshake says when it was the token that was refused —
 * `TOKEN_REFUSED` in `server/src/realtime/realtime.gateway.ts`.
 */
const TOKEN_REFUSED = 'token_invalid';

/**
 * How long to wait after `refusals` refusals in a row, in milliseconds: not at all
 * after the first, which is the routine stale token, then doubling up to a minute.
 */
export function waitAfter(refusals: number): number {
  return refusals <= 1 ? 0 : Math.min(60, 2 ** Math.min(refusals - 1, 6)) * 1000;
}

/**
 * How the channel stands, as `ChannelState` in `live_channel.dart` says it: `closed`
 * with nobody signed in, `connecting` for a moment after a drop — a phone between two
 * masts — `down` once that has gone on longer than {@link PATIENCE_MS}, `live` in.
 */
export type ChannelState = 'closed' | 'connecting' | 'down' | 'live';

/** How long `connecting` lasts before it is called `down`. */
export const PATIENCE_MS = 5_000;

/** A message as `message.new` carries it, with what a notification says of it. */
export interface HeardMessage {
  rideId: string;
  number?: number;
  senderName?: string | null;
  text?: string | null;
  kind?: string;
  storageKey?: string | null;
}

/** A ride the driver cancelled, as `ride.cancelled` carries it. */
export interface HeardCancel {
  rideId: string;
  number: number;
  from?: string | null;
  to?: string | null;
}

/** What `presence` tells the server about a tab. */
export interface Presence {
  visible: boolean;
  endpoint: string | null;
}

/** An answer as `support.answered` carries it. */
export interface HeardAnswer {
  threadId: string;
  number?: number;
  text?: string;
}

/**
 * The live channel, mirroring `apps/rider/lib/core/realtime.dart`.
 *
 * It is what turns «Заказ принят» into «Водитель выехал» without a pull-to-refresh,
 * and it is the only reason the лента поездки feels live. The payload is deliberately
 * an id and nothing else — each side refetches through its own view, so a passenger
 * can never be handed a field that was meant for the driver.
 *
 * **Every notification a tab shows comes from here.** With no tab in front of
 * anybody, the browser is told by Web Push instead (ADR 0027), and the tab says which
 * of the two it is with `present`: in front of somebody, the server leaves this
 * browser to the socket; not, and it pushes. Push reaches only a browser that allowed
 * notifications and subscribed, and through Google's, Apple's or Mozilla's servers, so
 * «Ищем машину» still promises nothing: the socket is what this app counts on, and
 * push is what it adds where it can.
 *
 * socket.io retries a dropped connection on its own, but not a handshake the server
 * refused — and the access token lives a quarter of an hour, so a tab that wakes
 * after that is refused as a matter of course. So a refusal that names the token
 * renews it and tries again, and any other refusal only waits and tries again.
 * Refusals in a row wait longer each time, so a server that turns away even a fresh
 * token is asked once a minute rather than in a loop. The twin of
 * `packages/limuzin_core/lib/src/live_channel.dart`.
 */
export class Realtime {
  private socket: Socket | null = null;
  private retry: ReturnType<typeof setTimeout> | null = null;
  private patience: ReturnType<typeof setTimeout> | null = null;
  private current: ChannelState = 'closed';

  /** How the channel stands now. */
  get state(): ChannelState {
    return this.current;
  }

  /**
   * Each change to `state`. What the server said while the channel was not `live`
   * was said to nobody, so its return is when the app asks again.
   */
  onState?: (state: ChannelState) => void;

  /** `open` is socket.io's `io`, and a seam for tests. */
  constructor(private readonly open: typeof io = io) {}

  /** Fired with the ride id that changed, or with null for «something changed». */
  onRide?: (rideId: string) => void;
  /**
   * The driver wrote in a ride's chat. The ride's number and the driver's short
   * name travel with it, for the notification.
   */
  onMessage?: (message: HeardMessage) => void;
  /**
   * The driver cancelled a ride from their «Отказ» sheet. `ride.updated` comes with
   * it; this is what says why, with the number and the route.
   */
  onCancelled?: (cancel: HeardCancel) => void;
  /**
   * The driver who had taken a предзаказ gave it up, and it is looking for another.
   * `ride.updated` comes with it and turns the card back; this is what says so. The
   * same payload as a cancel's.
   */
  onRefused?: (refusal: HeardCancel) => void;
  /** The passenger read a ride's chat on another screen: the dot comes off. */
  onRead?: (rideId: string) => void;
  /** «Поддержка» answered something: the thread, its number and the answer. */
  onSupport?: (answer: HeardAnswer) => void;
  /** Messages in a ride's chat are gone — the driver deleted them for both sides. */
  onDeleted?: (rideId: string, messageIds: string[]) => void;
  /** And in a conversation with «Поддержка». */
  onSupportDeleted?: (threadId: string, messageIds: string[]) => void;

  /** What the server was last told about this tab — see `present`. */
  private presence: Presence = { visible: false, endpoint: null };

  /**
   * Whether this tab is in front of somebody, and which browser it is: the Web Push
   * endpoint the server knows it by, once it has one. The server pushes to this
   * browser only while no tab of it is visible (ADR 0027). Said again on every
   * connection, socket.io's own reconnects included, because a new socket starts
   * as nobody watching.
   */
  present(change: Partial<Presence>): void {
    this.presence = { ...this.presence, ...change };
    if (this.socket?.connected) this.socket.emit('presence', this.presence);
  }

  /** Opens the channel for the session, taking each handshake's token from `token`. */
  connect(token: AccessTokenSource): void {
    this.disconnect();

    /** What the last handshake carried, to hand back if the server refuses it. */
    let sent: string | null = null;
    /** Refusals in a row, since the server last let the socket in. */
    let refusals = 0;

    // Same origin: dev proxies `/rt` through Vite, and in production the bundle is
    // served beside the API. `VITE_API_BASE` moves both if they are ever apart.
    const base = import.meta.env.VITE_API_BASE ?? '';
    const socket = this.open(`${base}/rt`, {
      // Websocket only. The polling fallback would reconnect through the token in a
      // query string, and a token in a URL is a token in somebody's access log.
      transports: ['websocket'],
      // Asked at every handshake, socket.io's own reconnects included, so a
      // connection that comes back carries the session as it is now.
      auth: (send) => {
        void token()
          .catch(() => null)
          .then((current) => {
            // Closed or replaced while the token was being read.
            if (this.socket !== socket) return;
            sent = current;
            send({ token: current });
          });
      },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10_000,
      autoConnect: false,
    });

    socket.on('connect', () => {
      refusals = 0;
      this.stopRetry();
      this.stopPatience();
      this.become('live');
      socket.emit('presence', this.presence);
    });
    // socket.io is already at getting back in; this only starts the clock.
    socket.on('disconnect', () => {
      if (this.socket === socket && this.current === 'live') this.lost();
    });
    socket.on('connect_error', (error: Error) => {
      // A connection that failed is socket.io's to retry, and it will. A handshake
      // the server refused leaves the socket inactive, and is this class's.
      if (socket.active) return;
      const renew = error.message === TOKEN_REFUSED;
      const rejected = sent;
      refusals += 1;
      this.stopRetry();
      this.retry = setTimeout(() => {
        this.retry = null;
        void (async () => {
          // Closed, replaced, or back in by itself while this waited.
          if (this.socket !== socket || socket.connected) return;
          if (renew) {
            const fresh = await token(rejected).catch(() => rejected);
            // No session any more, or the channel was closed while it was renewed.
            if (fresh === null || this.socket !== socket) return;
          }
          socket.connect();
        })();
      }, waitAfter(refusals));
    });

    socket.on('ride.updated', (data: { id?: string }) => {
      if (data?.id) this.onRide?.(data.id);
    });
    socket.on('ride.cancelled', (data: Partial<HeardCancel>) => {
      if (data?.rideId && typeof data.number === 'number') this.onCancelled?.(data as HeardCancel);
    });
    socket.on('ride.refused', (data: Partial<HeardCancel>) => {
      if (data?.rideId && typeof data.number === 'number') this.onRefused?.(data as HeardCancel);
    });
    socket.on('message.new', (data: Partial<HeardMessage>) => {
      if (data?.rideId) this.onMessage?.(data as HeardMessage);
    });
    socket.on('messages.read', (data: { rideId?: string }) => {
      if (data?.rideId) this.onRead?.(data.rideId);
    });
    socket.on('support.answered', (data: Partial<HeardAnswer>) => {
      if (data?.threadId) this.onSupport?.(data as HeardAnswer);
    });
    socket.on('messages.deleted', (data: { rideId?: string; messageIds?: string[] }) => {
      if (data?.rideId) this.onDeleted?.(data.rideId, data.messageIds ?? []);
    });
    socket.on(
      'support.messages.deleted',
      (data: { threadId?: string; messageIds?: string[] }) => {
        if (data?.threadId) this.onSupportDeleted?.(data.threadId, data.messageIds ?? []);
      },
    );

    this.socket = socket;
    this.lost();
    socket.connect();
  }

  disconnect(): void {
    this.stopRetry();
    this.stopPatience();
    this.become('closed');
    this.socket?.removeAllListeners();
    this.socket?.disconnect();
    this.socket = null;
  }

  get connected(): boolean {
    return this.socket?.connected ?? false;
  }

  private stopRetry(): void {
    if (this.retry !== null) clearTimeout(this.retry);
    this.retry = null;
  }

  /** Not in, as of now: `connecting` until {@link PATIENCE_MS} runs out. */
  private lost(): void {
    this.become('connecting');
    this.stopPatience();
    this.patience = setTimeout(() => {
      this.patience = null;
      if (this.current === 'connecting') this.become('down');
    }, PATIENCE_MS);
  }

  private stopPatience(): void {
    if (this.patience !== null) clearTimeout(this.patience);
    this.patience = null;
  }

  private become(next: ChannelState): void {
    if (next === this.current) return;
    this.current = next;
    this.onState?.(next);
  }
}
